import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  correct_answer?: number;
  part_id?: number;
}

interface part {
  id: number;
  titre: string;
  ordre: number;
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
}

const ManualEntry: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [parts, setparts] = useState<part[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  // Informations de l'étudiant
  const [studentFirstName, setStudentFirstName] = useState<string>('');
  const [studentLastName, setStudentLastName] = useState<string>('');

  // Réponses de l'étudiant (question_id => answer_number)
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});

  // Convertir un nombre en lettre (1 -> A, 2 -> B, etc.)
  const numberToLetter = (num: number): string => {
    return String.fromCharCode(64 + num); // 65 = A
  };

  useEffect(() => {
    fetchTestAndQuestions();
  }, [testId]);

  const fetchTestAndQuestions = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('manualEntry.errorLoadingTest'));
      }

      const data = await response.json();
      setTest(data.test);
      setQuestions(data.test.questions);
      setparts(data.test.parts || []);
      setLoading(false);

    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: number, answerNumber: number): void => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerNumber
    }));
  };

  const handleBack = (): void => {
    navigate(`/test-results/${testId}`);
  };

  const handleSubmit = async (): Promise<void> => {
    // Validation
    if (!studentFirstName.trim() || !studentLastName.trim()) {
      return;
    }

    if (Object.keys(answers).length !== questions.length) {
      return;
    }

    const confirmed = window.confirm(
      t('manualEntry.validation.confirmSubmit', { firstName: studentFirstName, lastName: studentLastName })
    );

    if (!confirmed) return;

    setSaving(true);

    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/manual-entry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          testId: parseInt(testId!),
          firstName: studentFirstName.trim(),
          lastName: studentLastName.trim(),
          answers: answers
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('manualEntry.submit.errorSaving'));
      }

      navigate(`/test-results/${testId}`);

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Vérifier si toutes les réponses sont remplies
  const allAnswersFilled = Object.keys(answers).length === questions.length;
  const canSubmit = studentFirstName.trim() !== '' && studentLastName.trim() !== '' && allAnswersFilled;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('manualEntry.loadingTest')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('manualEntry.testNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              {t('manualEntry.pageTitle', { testTitle: test.title })}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Informations de l'étudiant */}
          <div className="mb-8 bg-blue-50 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('manualEntry.studentInfo.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manualEntry.studentInfo.firstName')}
                </label>
                <input
                  type="text"
                  value={studentFirstName}
                  onChange={(e) => setStudentFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('manualEntry.studentInfo.firstNamePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manualEntry.studentInfo.lastName')}
                </label>
                <input
                  type="text"
                  value={studentLastName}
                  onChange={(e) => setStudentLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('manualEntry.studentInfo.lastNamePlaceholder')}
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('manualEntry.answersSection.title')}
            </h2>
            <p className="text-gray-600">
              {t('manualEntry.answersSection.instruction')}
            </p>
          </div>

          {/* Tableau des questions */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.answer')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.question')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('manualEntry.table.answer')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Diviser les questions en 4 groupes égaux
                  const quarterSize = Math.ceil(questions.length / 4);
                  const group1 = questions.slice(0, quarterSize);
                  const group2 = questions.slice(quarterSize, quarterSize * 2);
                  const group3 = questions.slice(quarterSize * 2, quarterSize * 3);
                  const group4 = questions.slice(quarterSize * 3);

                  // Nombre de lignes = taille du plus grand groupe
                  const maxRows = Math.max(group1.length, group2.length, group3.length, group4.length);

                  return Array.from({ length: maxRows }).map((_, rowIndex) => {
                    const q1 = group1[rowIndex];
                    const q2 = group2[rowIndex];
                    const q3 = group3[rowIndex];
                    const q4 = group4[rowIndex];

                    const renderQuestion = (q: typeof q1) => {
                      if (!q) {
                        return (
                          <>
                            <td className="border border-gray-300 px-2 py-2"></td>
                            <td className="border border-gray-300 px-2 py-2"></td>
                          </>
                        );
                      }

                      return (
                        <>
                          <td className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-semibold text-sm">
                            Q{q.question_number}
                          </td>
                          <td className="border border-gray-300 px-2 py-2">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {Array.from({ length: q.number_of_answers }).map((_, answerIndex) => {
                                const answerNumber = answerIndex + 1;
                                const answerLetter = numberToLetter(answerNumber);
                                const isSelected = answers[q.id] === answerNumber;
                                const colorClass = isSelected ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 hover:bg-gray-400';

                                return (
                                  <button
                                    key={answerNumber}
                                    onClick={() => handleAnswerSelect(q.id, answerNumber)}
                                    disabled={saving}
                                    className={`
                                      w-8 h-8 rounded-lg font-bold transition text-sm
                                      ${colorClass}
                                      ${isSelected ? 'text-white' : 'text-gray-700'}
                                      ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                                  >
                                    {answerLetter}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </>
                      );
                    };

                    return (
                      <tr
                        key={rowIndex}
                        className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-blue-50`}
                      >
                        {renderQuestion(q1)}
                        {renderQuestion(q2)}
                        {renderQuestion(q3)}
                        {renderQuestion(q4)}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {/* Bouton de validation */}
          <div className="flex justify-end mt-8">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
              className={`flex items-center gap-2 font-semibold py-3 px-8 rounded-lg transition ${
                canSubmit && !saving
                  ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check className="w-5 h-5" />
              {saving ? t('manualEntry.submit.saving') : t('manualEntry.submit.button')}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManualEntry;
